<template>
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="layout-header">
      <div class="header-left">
        <div class="patient-info">
          <IconCircle :initials="patientIcon" :size="32" bg-color="var(--icon-patient-bg)" color="var(--icon-patient-color)" />
          <h2 class="patient-name">{{ patientDisplayName }}</h2>
        </div>

        <TabBar :tabs="availableTabs" :active-tab="activeTab" @update:active-tab="handleTabChange" />
      </div>

      <div class="header-right">
        <template v-if="mode === 'view' && activeTab === 'details'">
          <button class="button primary" @click="enterEditMode">Edit Patient</button>
        </template>

        <template v-else-if="mode === 'view' && activeTab === 'tickets'">
          <button class="button primary" @click="handleCreateTicket">Create Ticket</button>
        </template>

        <template v-else-if="mode === 'edit' || mode === 'create'">
          <button class="button" @click="cancelEdit">Cancel</button>
          <button class="button primary" @click="saveChanges">
            {{ mode === 'create' ? 'Create Patient' : 'Save Changes' }}
          </button>
        </template>
      </div>
    </div>

    <div class="layout-body">
      <div v-if="activeTab === 'details'" class="card-container">
        <!-- Analytics Cards -->
        <div v-if="mode === 'view' && !isLoadingAnalytics" class="analytics-row">
          <CardLineChart
            class="analytics-card"
            title="Spend Over Time"
            :subtitle="'Total: ' + totalSpendFormatted"
            :data="spendOverTimeChartData"
            :options="spendOverTimeChartOptions"
            :formatTooltipValue="formatCurrencyValue"
            :height="200"
            variant="cumulative"
          />
          <CardBarChart
            class="analytics-card"
            title="Spend by Product"
            subtitle="Top 5 products"
            :data="spendByProductChartData"
            :options="spendByProductChartOptions"
            :horizontal="true"
            :height="200"
            :showLegend="false"
          />
          <CardBarChart
            class="analytics-card"
            title="Spend by Counselor"
            subtitle="Top 5 counselors"
            :data="spendByCounselorChartData"
            :options="spendByCounselorChartOptions"
            :horizontal="true"
            :height="200"
            :showLegend="false"
          />
        </div>

        <CardInputForm title="Personal Info" v-model="draft" :fields="personalInfoFields">
          <template #field-firstName>
            <TextInput v-model="draft.firstName" placeholder="Enter first name" />
          </template>
          <template #display-firstName="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-lastName>
            <TextInput v-model="draft.lastName" placeholder="Enter last name" />
          </template>
          <template #display-lastName="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-dateOfBirth>
            <DateInput v-model="draft.dateOfBirth" />
          </template>
          <template #display-dateOfBirth="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatUtils.formatDate(value) || 'No data' }}</span>
          </template>

          <template #field-age>
            <TextInput :disabled="true" :model-value="patientAge !== null ? `${patientAge} years` : 'No data'" />
          </template>
          <template #display-age>
            <span :class="{ 'no-data': patientAge === null }">
              {{ patientAge !== null ? `${patientAge} years` : 'No data' }}
            </span>
          </template>
        </CardInputForm>

        <CardInputForm title="Contact Info" v-model="draft" :fields="contactInfoFields">
          <template #field-email>
            <TextInput v-model="draft.email" placeholder="email@example.com" />
          </template>
          <template #display-email="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-mobilePhone>
            <PhoneNumberInput v-model="draft.mobilePhone" placeholder="(555) 555-5555" />
          </template>
          <template #display-mobilePhone="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatUtils.formatPhoneNumber(value) || 'No data' }}</span>
          </template>

          <template #field-homePhone>
            <PhoneNumberInput v-model="draft.homePhone" placeholder="(555) 555-5555" />
          </template>
          <template #display-homePhone="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatUtils.formatPhoneNumber(value) || 'No data' }}</span>
          </template>

          <template #field-workPhone>
            <PhoneNumberInput v-model="draft.workPhone" placeholder="(555) 555-5555" />
          </template>
          <template #display-workPhone="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatUtils.formatPhoneNumber(value) || 'No data' }}</span>
          </template>
        </CardInputForm>

        <CardInputForm title="Home Address" v-model="draft" :fields="addressFields">
          <template #field-addressStreet>
            <TextInput v-model="draft.addressStreet" placeholder="123 Main Street" />
          </template>
          <template #display-addressStreet="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-addressCity>
            <TextInput v-model="draft.addressCity" placeholder="Enter city" />
          </template>
          <template #display-addressCity="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-addressState>
            <TextInput v-model="draft.addressState" placeholder="Enter state" />
          </template>
          <template #display-addressState="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-addressZip>
            <TextInput v-model="draft.addressZip" placeholder="Enter ZIP code" />
          </template>
          <template #display-addressZip="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>
        </CardInputForm>

        <CardInputForm title="Other" v-model="draft" :fields="otherFields">
          <template #field-marketingSource>
            <SelectInput v-model="draft.marketingSource" :options="marketingSourceOptions" placeholder="Select marketing source" />
          </template>
          <template #display-marketingSource="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-notes>
            <TextareaInput v-model="draft.notes" placeholder="Enter notes..." :min-rows="3" />
          </template>
          <template #display-notes="{ value }">
            <span :class="{ 'no-data': !value }" class="notes-display">{{ value || 'No data' }}</span>
          </template>
        </CardInputForm>
      </div>
      <div v-if="activeTab === 'tickets'" class="tickets-container">
        <CustomTable
          :columns="visibleTicketColumnsFiltered"
          :rows="tickets"
          :sticky-first-column="true"
          :loading="isLoadingTickets"
          :is-empty="tickets.length === 0"
          :sort-by="ticketSortBy"
          :sort-order="ticketSortOrder"
          @row-click="handleTicketRowClick"
          @sort="handleTicketSort"
        >
          <template #column-ticketNumber="{ row }">
            <span class="cell-value name-text">Ticket #{{ row.ticketId }}</span>
          </template>

          <template #column-date="{ row }">
            <span class="cell-value">{{ formatUtils.formatDate(row.dateTicketStart) }}</span>
          </template>

          <template #column-ticketType="{ row }">
            <span class="cell-value">{{ getTicketType(row) }}</span>
          </template>

          <template #column-counselor="{ row }">
            <span class="cell-value">{{ row.counselorName }}</span>
          </template>

          <template #column-status="{ row }">
            <StatusBadge :status="calculateTicketStatus(row)" />
          </template>

          <template #column-products="{ row }">
            <span class="cell-value products-cell">{{ formatProducts(row.products) }}</span>
          </template>

          <template #column-paymentDuration="{ row }">
            <span class="cell-value">{{ formatPaymentDuration(row) }}</span>
          </template>

          <template #column-total="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsTotal) }}</span>
          </template>

          <template #column-collected="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCollected) }}</span>
          </template>

          <template #column-cancelled="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCancelled) }}</span>
          </template>

          <template #column-balance="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsBalance) }}</span>
          </template>

          <template #column-dateNextPayment="{ row }">
            <span v-if="row.dateNextReceivable" class="cell-value">{{ formatUtils.formatDate(row.dateNextReceivable) }}</span>
            <span v-else class="no-data">No balance</span>
          </template>

        </CustomTable>
      </div>
    </div>

    <div v-if="activeTab === 'tickets'" class="layout-footer">
      <PaginationControl
        :current-page="currentPage"
        :total-items="totalTickets"
        :items-per-page="itemsPerPage"
        @page-change="handlePageChange"
      />
    </div>

  </AppLayout>
</template>

<script>
import AppLayout from '@/components/app/AppLayout.vue';
import TabBar from '@/components/common/TabBar.vue';
import CardInputForm from '@/components/common/CardInputForm.vue';
import CardLineChart from '@/components/common/CardLineChart.vue';
import CardBarChart from '@/components/common/CardBarChart.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import StatusBadge from '@/components/common/StatusBadge.vue';
import CustomTable from '@/components/common/CustomTable.vue';
import PaginationControl from '@/components/common/PaginationControl.vue';
import PhoneNumberInput from '@/components/inputs/PhoneNumberInput.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import TextareaInput from '@/components/inputs/TextareaInput.vue';
import DateInput from '@/components/inputs/DateInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { calculateTicketStatus } from '@/utils/formatters';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';

export default {
  name: 'PatientRecordView',
  computed: {
    patientId() {
      return this.$route.params.id;
    },
    patientLink() {
      return '/patients/' + this.patientId;
    },
    breadcrumbs() {
      return [
        { label: 'Patients', link: '/patients' },
        { label: this.patientDisplayName || 'Patient', link: this.patientLink }
      ];
    },
    patientDisplayName() {
      if (this.mode === 'create') return 'New Patient';
      if (!this.patient) return 'Loading...';
      const firstName = this.patient.firstName || '';
      const lastName = this.patient.lastName || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown Patient';
    },
    patientIcon() {
      if (this.mode === 'create') return 'NP';
      if (!this.patient) return 'U';
      const firstName = this.patient.firstName || '';
      const lastName = this.patient.lastName || '';
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U';
    },
    isReadonly() {
      return this.mode === 'view';
    },
    visibleTicketColumnsFiltered() {
      return this.ticketColumns.filter(col => {
        // Always show first column (ticketNumber)
        if (col.key === 'ticketNumber') return true;
        // Show column if visibility is not set (default true) or explicitly true
        return this.ticketColumnVisibility[col.key] !== false;
      });
    },
    patientAge() {
      if (!this.draft.dateOfBirth) return null;

      const today = new Date();
      const birthDate = new Date(this.draft.dateOfBirth);

      if (isNaN(birthDate.getTime())) return null;

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      // Adjust age if birthday hasn't occurred yet this year
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    },
    personalInfoFields() {
      return [
        {
          name: 'firstName',
          label: 'First Name',
          readonly: this.isReadonly,
          required: true
        },
        {
          name: 'lastName',
          label: 'Last Name',
          readonly: this.isReadonly,
          required: true
        },
        {
          name: 'dateOfBirth',
          label: 'Date of Birth',
          readonly: this.isReadonly,
          required: true
        },
        {
          name: 'age',
          label: 'Age',
          readonly: this.isReadonly
        }
      ];
    },
    contactInfoFields() {
      return [
        {
          name: 'email',
          label: 'Email Address',
          readonly: this.isReadonly
        },
        {
          name: 'mobilePhone',
          label: 'Mobile Phone',
          readonly: this.isReadonly
        },
        {
          name: 'homePhone',
          label: 'Home Phone',
          readonly: this.isReadonly
        },
        {
          name: 'workPhone',
          label: 'Work Phone',
          readonly: this.isReadonly
        }
      ];
    },
    addressFields() {
      return [
        {
          name: 'addressStreet',
          label: 'Street Address',
          readonly: this.isReadonly
        },
        {
          name: 'addressCity',
          label: 'City',
          readonly: this.isReadonly
        },
        {
          name: 'addressState',
          label: 'State',
          readonly: this.isReadonly
        },
        {
          name: 'addressZip',
          label: 'ZIP Code',
          readonly: this.isReadonly
        }
      ];
    },
    otherFields() {
      return [
        {
          name: 'marketingSource',
          label: 'Marketing Source',
          readonly: this.isReadonly
        },
        {
          name: 'notes',
          label: 'Notes',
          readonly: this.isReadonly
        }
      ];
    },
    availableTabs() {
      const isEditMode = this.mode === 'create' || this.mode === 'edit';
      return [
        { key: 'details', label: 'Details', disabled: false },
        { key: 'tickets', label: 'Tickets', disabled: isEditMode }
      ];
    },
    activeTab() {
      const tab = this.$route.params.tab;
      const validTabs = this.availableTabs.map(t => t.key);
      if (tab && validTabs.includes(tab)) {
        return tab;
      }
      return 'details';
    },

    // Analytics chart data
    spendOverTimeChartData() {
      if (!this.analytics?.monthlySpend || this.analytics.monthlySpend.length === 0) return null;

      return {
        labels: this.analytics.monthlySpend.map(m => m.month),
        datasets: [
          {
            label: 'Spend',
            data: this.analytics.monthlySpend.map(m => m.amountCents / 100),
            color: '#4338ca'
          }
        ]
      };
    },

    spendOverTimeChartOptions() {
      return {
        yAxis: {
          axisLabel: {
            formatter: (value) => this.formatCurrencyShort(value)
          }
        }
      };
    },

    totalSpendFormatted() {
      if (!this.analytics?.totalSpend) return '$0.00';
      return this.formatCurrencyValue(this.analytics.totalSpend.amountCentsTotal / 100);
    },

    spendByProductChartData() {
      if (!this.analytics?.topProducts || this.analytics.topProducts.length === 0) return null;

      const topProducts = this.analytics.topProducts.slice(0, 5);
      const otherProducts = this.analytics.topProducts.slice(5);

      const chartData = [...topProducts];
      if (otherProducts.length > 0) {
        const otherTotal = otherProducts.reduce((sum, p) => sum + p.amountCentsTotal, 0);
        if (otherTotal > 0) {
          chartData.push({
            productId: -1,
            productName: 'Other',
            amountCentsTotal: otherTotal
          });
        }
      }

      // Reverse so highest is at top in horizontal bar
      const reversed = chartData.reverse();

      return {
        labels: reversed.map(p => p.productName),
        datasets: [
          {
            label: 'Spend',
            data: reversed.map(p => p.amountCentsTotal / 100),
            color: '#4338ca'
          }
        ]
      };
    },

    spendByProductChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];
            const param = params[0];
            const value = param.value || 0;
            return `<div style="font-weight: 600; margin-bottom: 8px;">${param.axisValue}</div>
              <div style="display: flex; align-items: center; margin-bottom: 4px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
                <span style="flex: 1;">Spend:</span>
                <span style="font-weight: 600; margin-left: 12px;">${this.formatCurrencyValue(value)}</span>
              </div>`;
          }
        }
      };
    },

    spendByCounselorChartData() {
      if (!this.analytics?.topCounselors || this.analytics.topCounselors.length === 0) return null;

      const topCounselors = this.analytics.topCounselors.slice(0, 5);
      const otherCounselors = this.analytics.topCounselors.slice(5);

      const chartData = [...topCounselors];
      if (otherCounselors.length > 0) {
        const otherTotal = otherCounselors.reduce((sum, c) => sum + c.amountCentsTotal, 0);
        if (otherTotal > 0) {
          chartData.push({
            counselorId: -1,
            counselorName: 'Other',
            amountCentsTotal: otherTotal
          });
        }
      }

      // Reverse so highest is at top in horizontal bar
      const reversed = chartData.reverse();

      return {
        labels: reversed.map(c => c.counselorName),
        datasets: [
          {
            label: 'Spend',
            data: reversed.map(c => c.amountCentsTotal / 100),
            color: '#4338ca'
          }
        ]
      };
    },

    spendByCounselorChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];
            const param = params[0];
            const value = param.value || 0;
            return `<div style="font-weight: 600; margin-bottom: 8px;">${param.axisValue}</div>
              <div style="display: flex; align-items: center; margin-bottom: 4px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
                <span style="flex: 1;">Spend:</span>
                <span style="font-weight: 600; margin-left: 12px;">${this.formatCurrencyValue(value)}</span>
              </div>`;
          }
        }
      };
    }
  },
  data() {
    return {
      patient: null,
      isLoading: false,
      mode: 'view',
      draft: {},
      analytics: null,
      isLoadingAnalytics: false,
      formatUtils,
      marketingSourceOptions: [
        { value: '101.1 ESPN', label: '101.1 ESPN' },
        { value: '1120 KMOX', label: '1120 KMOX' },
        { value: '90.7 NPR', label: '90.7 NPR' },
        { value: '94.7 KSHE', label: '94.7 KSHE' },
        { value: '97.1 KFTK', label: '97.1 KFTK' },
        { value: 'Call Center', label: 'Call Center' },
        { value: 'Ch. 2 KTVI (Fox)', label: 'Ch. 2 KTVI (Fox)' },
        { value: 'Ch. 4 KMOV (CBS)', label: 'Ch. 4 KMOV (CBS)' },
        { value: 'Ch. 5 KSDK (NBC)', label: 'Ch. 5 KSDK (NBC)' },
        { value: 'Current Patient', label: 'Current Patient' },
        { value: 'Direct Mailer', label: 'Direct Mailer' },
        { value: 'KPLR- Channel 11', label: 'KPLR- Channel 11' },
        { value: 'Online/Website', label: 'Online/Website' },
        { value: 'Post Dispatch', label: 'Post Dispatch' },
        { value: 'Referral', label: 'Referral' },
        { value: 'SHIPPING', label: 'SHIPPING' },
        { value: 'Uncertain Radio', label: 'Uncertain Radio' },
        { value: 'Uncertain TV', label: 'Uncertain TV' },
        { value: 'Other', label: 'Other' }
      ],
      // Ticket-related state
      tickets: [],
      totalTickets: 0,
      currentPage: 1,
      itemsPerPage: 100,
      isLoadingTickets: false,
      ticketSortBy: 'dateTicketStart',
      ticketSortOrder: 'desc',
      ticketColumnVisibility: {},
      ticketColumns: [
        { key: 'ticketNumber', label: 'Ticket ID', width: '200px' },
        { key: 'date', label: 'Ticket Date', width: '200px', sortable: true, sortKey: 'dateTicketStart' },
        { key: 'ticketType', label: 'Ticket Type', width: '200px' },
        { key: 'counselor', label: 'Counselor', width: '200px' },
        { key: 'status', label: 'Status', width: '200px' },
        { key: 'products', label: 'Products', width: '200px' },
        { key: 'paymentDuration', label: 'Payment Duration', width: '200px' },
        { key: 'total', label: 'Total Amount', width: '200px', sortable: true, sortKey: 'amountCentsTotal' },
        { key: 'collected', label: 'Amount Collected', width: '200px', sortable: true, sortKey: 'amountCentsCollected' },
        { key: 'cancelled', label: 'Amount Cancelled', width: '200px', sortable: true, sortKey: 'amountCentsCancelled' },
        { key: 'balance', label: 'Current Balance', width: '200px', sortable: true, sortKey: 'amountCentsBalance' },
        { key: 'dateNextPayment', label: 'Date Next Payment', width: '200px', sortable: true, sortKey: 'dateNextReceivable' }
      ]
    };
  },
  watch: {
    currentPage() {
      this.loadTickets();
    },
    itemsPerPage() {
      this.loadTickets();
    },
    activeTab: {
      handler(newTab) {
        if (newTab === 'tickets') {
          this.loadTickets();
        }
      },
      immediate: true
    },
    ticketSortBy() {
      this.currentPage = 1;
      this.loadTickets();
    },
    ticketSortOrder() {
      this.currentPage = 1;
      this.loadTickets();
    }
  },
  created() {
    this.loadTicketColumnVisibility();
    if (this.patientId === 'new') {
      this.enterCreateMode();
    } else {
      this.load();
    }
  },
  methods: {
    async load() {
      try {
        this.isLoading = true;

        const patient = await queryClient.fetchQuery({
          queryKey: ['patient', this.patientId],
          queryFn: () => api.fetchPatientById(this.patientId)
        });

        this.patient = patient;
        this.draft = structuredClone(this.patient);

        // Load analytics for existing patients
        this.loadAnalytics();

      } catch (err) {
        console.error('Failed to fetch patient:', err);
        alertService.error('Failed to load patient: ' + err.message);
        this.$router.push('/patients');
      } finally {
        this.isLoading = false;
      }
    },
    async loadAnalytics() {
      if (this.mode === 'create') return;

      try {
        this.isLoadingAnalytics = true;
        const analytics = await queryClient.fetchQuery({
          queryKey: ['patient-analytics', this.patientId],
          queryFn: () => api.fetchPatientAnalytics(this.patientId)
        });
        this.analytics = analytics;
      } catch (err) {
        console.error('Failed to fetch patient analytics:', err);
      } finally {
        this.isLoadingAnalytics = false;
      }
    },
    formatCurrencyValue(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
    },
    formatCurrencyShort(value) {
      if (value >= 1000000) {
        return '$' + (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return '$' + (value / 1000).toFixed(0) + 'K';
      }
      return '$' + value.toFixed(2);
    },
    handleTabChange(tabKey) {
      const basePath = `/patients/${this.patientId}`;
      if (tabKey === 'details') {
        this.$router.push(basePath);
      } else {
        this.$router.push(`${basePath}/${tabKey}`);
      }
    },
    enterEditMode() {
      if (this.activeTab !== 'details') {
        this.$router.push(`/patients/${this.patientId}`);
      }
      this.draft = structuredClone(this.patient);
      this.mode = 'edit';
    },
    enterCreateMode() {
      this.draft = this.getEmptyDraft();
      this.mode = 'create';
    },
    cancelEdit() {
      if (this.mode === 'create') {
        this.$router.push('/patients');
      } else {
        this.draft = structuredClone(this.patient);
        this.mode = 'view';
      }
    },
    getEmptyDraft() {
      return {
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        marketingSource: '',
        email: '',
        mobilePhone: '',
        homePhone: '',
        workPhone: '',
        addressStreet: '',
        addressCity: '',
        addressState: '',
        addressZip: '',
        notes: ''
      };
    },
    transformDraftToPayload(draft) {
      return {
        firstName: draft.firstName,
        lastName: draft.lastName,
        dateOfBirth: draft.dateOfBirth || null,
        marketingSource: draft.marketingSource || null,
        email: draft.email || null,
        mobilePhone: draft.mobilePhone || null,
        homePhone: draft.homePhone || null,
        workPhone: draft.workPhone || null,
        addressStreet: draft.addressStreet || null,
        addressCity: draft.addressCity || null,
        addressState: draft.addressState || null,
        addressZip: draft.addressZip || null,
        notes: draft.notes || null
      };
    },
    async saveChanges() {
      try {
        const payload = this.transformDraftToPayload(this.draft);
        let updatedPatient;

        if (this.mode === 'edit') {
          updatedPatient = await api.updatePatient(this.patientId, payload);
          alertService.success('Patient updated successfully');
        }

        if (this.mode === 'create') {
          updatedPatient = await api.createPatient(payload);
          alertService.success('Patient created successfully');
          this.$router.replace(`/patients/${updatedPatient.patientId}`);
        }

        // Invalidate caches
        queryClient.invalidateQueries({ queryKey: ['patients'] });
        queryClient.invalidateQueries({ queryKey: ['patient', this.patientId] });

        // Update patient
        this.patient = updatedPatient;
        this.draft = structuredClone(this.patient);
        this.mode = 'view';

      } catch (err) {
        console.error('Failed to save patient:', err);
        alertService.error('Failed to save patient: ' + err.message);
      }
    },
    async loadTickets() {
      try {
        this.isLoadingTickets = true;

        const offset = (this.currentPage - 1) * this.itemsPerPage;
        const response = await queryClient.fetchQuery({
          queryKey: ['tickets', { patientId: this.patientId, limit: this.itemsPerPage, offset, sortBy: this.ticketSortBy, sortOrder: this.ticketSortOrder }],
          queryFn: () => api.fetchTickets({
            patientId: this.patientId,
            limit: this.itemsPerPage,
            offset,
            sortBy: this.ticketSortBy,
            sortOrder: this.ticketSortOrder
          })
        });

        this.tickets = response.tickets;
        this.totalTickets = response.total;

      } catch (err) {
        console.error('Failed to fetch tickets:', err);
        alertService.error('Failed to load tickets: ' + err.message);
        this.tickets = [];
        this.totalTickets = 0;
      } finally {
        this.isLoadingTickets = false;
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleTicketRowClick({ rowIndex }) {
      const ticket = this.tickets[rowIndex];
      if (ticket && ticket.ticketId) {
        this.$router.push(`/tickets/${ticket.ticketId}`);
      }
    },
    handleTicketSort({ sortBy, sortOrder }) {
      this.ticketSortBy = sortBy;
      this.ticketSortOrder = sortOrder;
    },
    getCounselorInitials(ticket) {
      const name = ticket.counselorName?.charAt(0) || '';
      return name.toUpperCase();
    },
    handleCreateTicket() {
      this.$router.push(`/tickets/new?patientId=${this.patientId}`);
    },
    calculateTicketStatus(ticket) {
      return calculateTicketStatus(ticket);
    },
    formatPaymentDuration(ticket) {
      if (!ticket.dateFirstReceivable || !ticket.dateLastReceivable) {
        return '—';
      }
      const first = new Date(ticket.dateFirstReceivable);
      const last = new Date(ticket.dateLastReceivable);
      const months = (last.getFullYear() - first.getFullYear()) * 12 + (last.getMonth() - first.getMonth()) + 1;
      if (months <= 1) {
        return 'Pay in Full';
      }
      return `${months} Months`;
    },
    formatProducts(products) {
      if (!products || products.length === 0) {
        return '—';
      }
      return products.map(p => `${p.name} (x${p.quantity})`).join(', ');
    },
    getTicketType(ticket) {
      return ticket.isFirstTicketForPatient ? 'New Patient' : 'Current Patient';
    },
    loadTicketColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        const saved = localStorage.getItem(`shawncrm_columns_${practiceId}_tickets`);
        if (saved) {
          this.ticketColumnVisibility = JSON.parse(saved);
        } else {
          // Default columns: Ticket Date, Ticket Type, Patient, Counselor, Status, Products, Total Amount, Current Balance
          // Note: Patient column doesn't exist in PatientRecordView, so it's excluded
          const defaultVisibleColumns = ['date', 'ticketType', 'counselor', 'status', 'products', 'total', 'balance'];
          this.ticketColumns.forEach(col => {
            this.ticketColumnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
          });
        }
      } catch (err) {
        console.error('Failed to load ticket column visibility:', err);
        // Default columns on error
        const defaultVisibleColumns = ['date', 'ticketType', 'counselor', 'status', 'products', 'total', 'balance'];
        this.ticketColumns.forEach(col => {
          this.ticketColumnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
        });
      }
    }
  },
  components: { AppLayout, TabBar, CardInputForm, CardLineChart, CardBarChart, IconCircle, StatusBadge, CustomTable, PaginationControl, PhoneNumberInput, TextInput, TextareaInput, DateInput, SelectInput }
}
</script>

<style scoped>
.analytics-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
}

.analytics-card {
  border-right: 1px solid #e5e7eb;
}

.analytics-card:last-child {
  border-right: none;
}

@media (max-width: 1024px) {
  .analytics-row {
    grid-template-columns: 1fr;
  }

  .analytics-card {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .analytics-card:last-child {
    border-bottom: none;
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  min-width: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.patient-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.patient-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.2;
}

.card-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}

.layout-header .button {
  white-space: nowrap;
}

/* Tickets tab container */
.tickets-container {
  height: 100%;
  width: 100%;
}

/* Cell styling */
.ticket-cell,
.counselor-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.name-text {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-value {
  color: #333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.products-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notes-display {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
